import React, { useEffect, useState, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { useInviteTheme } from './InviteThemeContext';
import { Sparkles, Plus, Search, Calendar, Users, Send, CheckCircle2, Clock, Trash2, ArrowUpRight, Edit3, ShieldCheck, Zap } from 'lucide-react';
import API_BASE_URL from '../../config';

interface Campaign {
    _id: string;
    eventName: string;
    eventType: string;
    eventDate: string;
    status: 'draft' | 'ready' | 'sending' | 'completed' | 'paused';
    totalRecipients: number;
    sentCount: number;
    rsvpAccepted: number;
    rsvpDeclined: number;
    channels: string[];
}

export const InviteDashboard: React.FC = () => {
    const history = useHistory();
    const { inviteToken, inviteUser } = useInviteAuth();
    const { theme } = useInviteTheme();
    const isLightTheme = theme === 'light';
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'completed'>('all');

    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/invite/campaigns`, {
                    headers: {
                        Authorization: `Bearer ${inviteToken}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to retrieve invitations');
                }

                const data = await response.json();
                setCampaigns(data.campaigns);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (inviteToken) {
            fetchCampaigns();
        }
    }, [inviteToken]);

    const handleDelete = async (id: string, eventName: string) => {
        if (!window.confirm(`Are you sure you want to delete "${eventName}"? All recipient details and RSVP records will be removed.`)) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${inviteToken}`,
                },
            });

            if (response.ok) {
                setCampaigns(campaigns.filter(c => c._id !== id));
            } else {
                alert('Failed to delete invitation');
            }
        } catch (err: any) {
            console.error('Delete error:', err.message);
        }
    };

    // Aggregated Metrics
    const totalSent = campaigns.reduce((acc, c) => acc + (c.sentCount || 0), 0);
    const totalAccepted = campaigns.reduce((acc, c) => acc + (c.rsvpAccepted || 0), 0);
    const activeCampaigns = campaigns.filter(c => c.status !== 'completed' && c.status !== 'draft').length;
    const rsvpRate = totalSent > 0 ? Math.round((totalAccepted / totalSent) * 100) : 0;

    // Filtered Campaigns
    const filteredCampaigns = useMemo(() => {
        return campaigns.filter(c => {
            const matchesSearch = c.eventName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                  c.eventType.toLowerCase().includes(searchQuery.toLowerCase());
            if (!matchesSearch) return false;

            if (statusFilter === 'all') return true;
            if (statusFilter === 'active') return c.status === 'ready' || c.status === 'sending';
            if (statusFilter === 'draft') return c.status === 'draft';
            if (statusFilter === 'completed') return c.status === 'completed';
            return true;
        });
    }, [campaigns, searchQuery, statusFilter]);

    // Event Emoji Helper
    const getEventEmoji = (type: string) => {
        switch (type) {
            case 'marriage': return '💒';
            case 'birthday': return '🎂';
            case 'housewarming': return '🏠';
            case 'anniversary': return '💍';
            case 'corporate': return '🏢';
            case 'engagement': return '💎';
            case 'baby-shower': return '👶';
            case 'retirement': return '🌴';
            default: return '✨';
        }
    };

    if (loading) {
        return (
            <div className="invite-loading-screen">
                <div className="spinner"></div>
                <p>Loading Studio Dashboard...</p>
            </div>
        );
    }

    return (
        <div className="invite-dashboard-modern">
            {/* Ambient Lighting Orbs */}
            <div className="ambient-orb orb-violet" />
            <div className="ambient-orb orb-emerald" />

            {/* 1. Hero Welcome Studio Banner */}
            <div className="modern-hero-banner">
                <div className="hero-banner-content">
                    <div className="hero-badge-row">
                        <span className="hero-chip chip-live">
                            <span className="pulse-dot" /> Studio Live
                        </span>
                        <span className="hero-chip chip-tier">
                            {inviteUser?.isPremium ? (
                                <>★ Pro Suite ({inviteUser.maxInvitations} Guests)</>
                            ) : (
                                <>🕊️ Free Starter Tier</>
                            )}
                        </span>
                    </div>

                    <h1 className="hero-title">
                        Welcome back, <span className="gradient-text">{inviteUser ? inviteUser.name : 'Creator'}</span>
                    </h1>
                    <p className="hero-subtitle">
                        Design digital wedding cards, launch AI video invites, and orchestrate automated WhatsApp & Telegram RSVPs.
                    </p>
                </div>

                <div className="hero-actions">
                    <Link to="/invite/new" className="hero-cta-btn">
                        <Plus size={18} />
                        <span>Create Invitation</span>
                        <Sparkles size={16} className="sparkle-icon" />
                    </Link>
                </div>
            </div>



            {/* 3. Modern KPI Analytics Grid */}
            <div className="modern-kpi-grid">
                {/* Total Invitations */}
                <div className="kpi-card kpi-violet">
                    <div className="kpi-top">
                        <span className="kpi-label">Total Events</span>
                        <div className="kpi-icon-wrap">
                            <Calendar size={18} />
                        </div>
                    </div>
                    <div className="kpi-value">{campaigns.length}</div>
                    <div className="kpi-footer">
                        <span className="kpi-trend trend-neutral">Campaigns tracked</span>
                    </div>
                </div>

                {/* Sent Count */}
                <div className="kpi-card kpi-cyan">
                    <div className="kpi-top">
                        <span className="kpi-label">Invites Dispatched</span>
                        <div className="kpi-icon-wrap">
                            <Send size={18} />
                        </div>
                    </div>
                    <div className="kpi-value">{totalSent}</div>
                    <div className="kpi-footer">
                        <span className="kpi-trend trend-positive">Across all channels</span>
                    </div>
                </div>

                {/* RSVP Accepted */}
                <div className="kpi-card kpi-emerald">
                    <div className="kpi-top">
                        <span className="kpi-label">Confirmed RSVPs</span>
                        <div className="kpi-icon-wrap">
                            <CheckCircle2 size={18} />
                        </div>
                    </div>
                    <div className="kpi-value">{totalAccepted}</div>
                    <div className="kpi-footer">
                        <span className="kpi-trend trend-emerald">
                            {rsvpRate}% Response rate
                        </span>
                    </div>
                </div>

                {/* Active Events */}
                <div className="kpi-card kpi-amber">
                    <div className="kpi-top">
                        <span className="kpi-label">Active Events</span>
                        <div className="kpi-icon-wrap">
                            <Clock size={18} />
                        </div>
                    </div>
                    <div className="kpi-value">{activeCampaigns}</div>
                    <div className="kpi-footer">
                        <span className="kpi-trend trend-amber">Live 12h countdowns</span>
                    </div>
                </div>
            </div>

            {/* 4. Controls Toolbar: Search & Filter Tabs */}
            <div className="modern-toolbar">
                <div className="toolbar-search">
                    <Search size={16} className="search-icon" />
                    <input 
                        type="text" 
                        placeholder="Search events by title or ceremony type..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search-btn" onClick={() => setSearchQuery('')}>✕</button>
                    )}
                </div>

                <div className="toolbar-filters">
                    <button 
                        className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('all')}
                    >
                        All ({campaigns.length})
                    </button>
                    <button 
                        className={`filter-chip ${statusFilter === 'active' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('active')}
                    >
                        Active ({campaigns.filter(c => c.status === 'ready' || c.status === 'sending').length})
                    </button>
                    <button 
                        className={`filter-chip ${statusFilter === 'draft' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('draft')}
                    >
                        Drafts ({campaigns.filter(c => c.status === 'draft').length})
                    </button>
                    <button 
                        className={`filter-chip ${statusFilter === 'completed' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('completed')}
                    >
                        Completed ({campaigns.filter(c => c.status === 'completed').length})
                    </button>
                </div>
            </div>

            {error && <div className="modern-error-banner">⚠️ {error}</div>}

            {/* 5. Modern Event Invitations Grid */}
            {filteredCampaigns.length === 0 ? (
                <div className="modern-empty-state">
                    <div className="empty-state-visual">
                        <span className="empty-emoji">💌</span>
                        <div className="empty-glow" />
                    </div>
                    <h3>{searchQuery ? 'No matching events found' : 'No invitations created yet'}</h3>
                    <p>
                        {searchQuery 
                            ? 'Try refining your search keyword or clearing status filters.' 
                            : 'Create your first personalized digital card, attach memories, and send live WhatsApp invitations in 60 seconds.'}
                    </p>
                    <Link to="/invite/new" className="empty-cta-btn">
                        <Plus size={16} /> Create Your First Invitation
                    </Link>
                </div>
            ) : (
                <div className="modern-campaigns-grid">
                    {filteredCampaigns.map(campaign => {
                        const eventRsvpPercent = campaign.sentCount > 0 
                            ? Math.min(100, Math.round((campaign.rsvpAccepted / campaign.sentCount) * 100)) 
                            : 0;

                        return (
                            <div key={campaign._id} className="modern-event-card">
                                {/* Card Header */}
                                <div className="event-card-header">
                                    <div className="event-type-badge">
                                        <span className="emoji-icon">{getEventEmoji(campaign.eventType)}</span>
                                        <span className="type-name">{campaign.eventType}</span>
                                    </div>
                                    <span className={`modern-status-badge status-${campaign.status}`}>
                                        <span className="status-dot" /> {campaign.status}
                                    </span>
                                </div>

                                {/* Card Body */}
                                <div className="event-card-body">
                                    <h3 className="event-title">{campaign.eventName}</h3>
                                    
                                    <div className="event-date-row">
                                        <Calendar size={14} className="date-icon" />
                                        <span>
                                            {new Date(campaign.eventDate).toLocaleDateString(undefined, {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                            })}
                                        </span>
                                    </div>

                                    {/* RSVP Progress Mini Bar */}
                                    <div className="event-progress-wrap">
                                        <div className="progress-labels">
                                            <span className="progress-text">RSVP Progress</span>
                                            <span className="progress-numbers">
                                                <strong>{campaign.rsvpAccepted}</strong> / {campaign.totalRecipients || 0} Confirmed
                                            </span>
                                        </div>
                                        <div className="progress-track">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${eventRsvpPercent}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Delivery Channels */}
                                    <div className="event-channels-row">
                                        {campaign.channels && campaign.channels.length > 0 ? (
                                            campaign.channels.map(ch => (
                                                <span key={ch} className={`channel-tag tag-${ch}`}>
                                                    {ch === 'whatsapp' ? '🟢 WhatsApp' : ch === 'sms' ? '🔵 SMS' : ch === 'ivr' ? '📞 Voice' : '✈️ Telegram'}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="channel-tag tag-whatsapp">🟢 WhatsApp</span>
                                        )}
                                    </div>
                                </div>

                                {/* Card Footer Actions */}
                                <div className="event-card-footer">
                                    <div className="recipient-pill">
                                        <Users size={13} />
                                        <span>{campaign.totalRecipients || 0} Guests</span>
                                    </div>

                                    <div className="card-actions-group">
                                        {campaign.status === 'draft' ? (
                                            <Link to={`/invite/new?edit=${campaign._id}`} className="card-btn-edit">
                                                <Edit3 size={13} /> Edit
                                            </Link>
                                        ) : (
                                            <Link to={`/invite/campaign/${campaign._id}`} className="card-btn-view">
                                                <span>Manage</span>
                                                <ArrowUpRight size={14} />
                                            </Link>
                                        )}
                                        
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(campaign._id, campaign.eventName); }} 
                                            className="card-btn-delete"
                                            title="Delete Campaign"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default InviteDashboard;

